import _ from "lodash";
import * as THREE from "three";
import { make_random_vector } from "./utils";
import { dice_inertia } from "./settings";
import { diceBodyMaterial } from "./diceBoxSimulation";
import { createDieWithPhysics } from "./createDice";

/* ====================================
 * Utility Functions for Dice Roll Prep
 * ====================================
 */

export const getDiceLaunchVectors = ({
  width,
  height,
  diceTypes,
  vector,
  boost
}) => {
  const vectors = diceTypes.map(dieType => {
    const vec = make_random_vector(vector);
    let pos = {
      x: width * (vec.x > 0 ? -1 : 1) * 0.9,
      y: height * (vec.y > 0 ? -1 : 1) * 0.9,
      z: Math.random() * 200 + 200
    };
    let projector = Math.abs(vec.x / vec.y);
    if (projector > 1.0) {
      pos.y /= projector;
    } else {
      pos.x *= projector;
    }
    let velvec = make_random_vector(vector);
    let velocity = { x: velvec.x * boost, y: velvec.y * boost, z: -10 };
    let inertia = dice_inertia[dieType];
    let angle = {
      x: -(Math.random() * vec.y * 5 + inertia * vec.y),
      y: Math.random() * vec.x * 5 + inertia * vec.x,
      z: 0
    };
    let axis = {
      x: Math.random(),
      y: Math.random(),
      z: Math.random(),
      a: Math.random()
    };

    return {
      type: dieType,
      pos,
      velocity,
      angle,
      axis
    };
  });

  return vectors;
};

export const getLaunchVectorAndBoost = ({
  viewWidth,
  viewHeight,
  swipeInfo = {}
}) => {
  const { swipeVector, swipeTime } = swipeInfo;

  const rawLaunchVector = swipeVector || {
    x: (Math.random() * 2 - 1) * viewWidth,
    y: -(Math.random() * 2 - 1) * viewHeight
  };
  const dist = Math.sqrt(
    rawLaunchVector.x * rawLaunchVector.x +
      rawLaunchVector.y * rawLaunchVector.y
  );
  const boost = swipeTime
    ? Math.sqrt((2500 - swipeTime) / 2500) * dist * 3
    : (Math.random() + 3) * dist;
  const launchVector = {
    x: rawLaunchVector.x / dist,
    y: rawLaunchVector.y / dist
  };

  return { launchVector, boost };
};

export const resetAnimationTracking = diceBox => {
  const animationTracking = {
    last_time: 0,
    iteration: 0,
    isAnimationRunning: false
  };
  return { ...diceBox, animationTracking };
};

export const prepAnimation = diceBox => {
  let animationPromiseResolver;
  let animationPromiseRejecter;
  const animationPromise = new Promise((resolve, reject) => {
    animationPromiseResolver = resolve;
    animationPromiseRejecter = reject;
  });

  let { animationTracking = {} } = diceBox;
  animationTracking = { ...animationTracking, isAnimationRunning: true };

  return {
    ...diceBox,
    animationPromise,
    animationPromiseResolver,
    animationPromiseRejecter,
    animationTracking
  };
};

export const prepareDiceForRoll = ({
  scale,
  diceTypesWithVectors,
  diceColor,
  labelColor
}) =>
  diceTypesWithVectors.map(dieWithVector =>
    createDieWithPhysics(dieWithVector, {
      scale,
      diceBodyMaterial,
      diceColor,
      labelColor
    })
  );

/* ===============================
 * Utility Functions for Animation
 * ===============================
 */

export const calculateTimeDifferenceInSeconds = (
  currentTime = 0,
  previousTime = 0,
  frame_rate = 1 / 60
) => {
  const time_diff = (currentTime - previousTime) / 1000;

  return time_diff <= frame_rate * 10
    ? Math.max(time_diff, frame_rate / 2)
    : frame_rate;
};

export const checkIfThrowFinished = diceBox => {
  const e = 0.1;
  const { rolledDice, animationTracking, frame_rate } = diceBox;
  const { iteration } = animationTracking;
  if (iteration <= 5 / frame_rate) {
    const stopIterations = _.compact(_.map(rolledDice, `die_stopped`));
    const mostRecentStop = _.size(stopIterations)
      ? Math.max(...stopIterations)
      : 0;

    rolledDice.forEach(die => {
      if (!die.die_stopped) {
        let a = die.body.angularVelocity,
          v = die.body.velocity;

        if (iteration > 100 && iteration >= mostRecentStop + 50) {
          if (Math.abs(a.x) < e && Math.abs(a.y) < e && Math.abs(a.z) < e) {
            die.die_stopped = iteration; // unsafe mutation via reference
          }
        } else {
          if (
            Math.abs(a.x) < e &&
            Math.abs(a.y) < e &&
            Math.abs(a.z) < e &&
            Math.abs(v.x) < e &&
            Math.abs(v.y) < e &&
            Math.abs(v.z) < e
          ) {
            die.die_stopped = iteration; // unsafe mutation via reference
          }
        }
      }
    });

    return rolledDice.every(die => !!die.die_stopped);
  } else {
    return true;
  }
};

export const render = diceBox => {
  const { renderer, scene, camera } = diceBox;
  const isValidRenderer =
    `test` === process.env.NODE_ENV
      ? _.get(renderer, `isValid`, false)
      : renderer instanceof THREE.WebGLRenderer;
  if (!isValidRenderer) {
    console.warn(
      `render() failed: diceBox.renderer is not a THREE.WebGLRenderer`
    );
    return;
  }
  if (!(scene instanceof THREE.Scene) || !(camera instanceof THREE.Camera)) {
    console.warn(
      `render() failed: diceBox.scene is not a THREE.Scene or diceBox.camera is not a THREE.Camera`
    );

    return;
  }
  renderer.render(scene, camera);
};
